import { Input, TabButton, Toggle } from '@components/common';
import { Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import {
  AccessControlArea,
  AccessControlRecord,
} from '../../../types/accessControl';

interface AccessControlChecklistProps {
  controls: AccessControlRecord[];
  selectedControlIds: string[];
  onToggleControl: (controlId: string) => void;
}

const AREA_LABELS: Record<AccessControlArea, string> = {
  [AccessControlArea.DASHBOARD]: 'Dashboard controls',
  [AccessControlArea.SALES]: 'Sales controls',
  [AccessControlArea.ACCOMMODATION]: 'Accommodation controls',
  [AccessControlArea.ACCOUNTING]: 'Accounting controls',
  [AccessControlArea.INVENTORY]: 'Inventory controls',
  [AccessControlArea.SETTINGS]: 'Settings controls',
};

const ALL_AREAS = Object.values(AccessControlArea);

const groupByArea = (
  controls: AccessControlRecord[],
): Record<AccessControlArea, AccessControlRecord[]> => {
  const grouped = controls.reduce<
    Record<AccessControlArea, AccessControlRecord[]>
  >(
    (accumulator, control) => {
      accumulator[control.area] = accumulator[control.area] || [];
      accumulator[control.area].push(control);
      return accumulator;
    },
    {
      [AccessControlArea.DASHBOARD]: [],
      [AccessControlArea.SALES]: [],
      [AccessControlArea.ACCOMMODATION]: [],
      [AccessControlArea.ACCOUNTING]: [],
      [AccessControlArea.INVENTORY]: [],
      [AccessControlArea.SETTINGS]: [],
    },
  );

  // Ascending by name so a control is easy to scan for within a category —
  // both call sites below (unfiltered tab counts, search-filtered render
  // list) share this one helper, so sorting here covers both at once.
  Object.values(grouped).forEach((list) =>
    list.sort((a, b) => a.name.localeCompare(b.name)),
  );

  return grouped;
};

export const AccessControlChecklist: React.FC<AccessControlChecklistProps> = ({
  controls,
  selectedControlIds,
  onToggleControl,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeArea, setActiveArea] = useState<AccessControlArea>(
    AccessControlArea.DASHBOARD,
  );

  // Unfiltered — drives which tabs exist and their total/selected counts.
  const allControlsByArea = useMemo(() => groupByArea(controls), [controls]);

  // Search-filtered — drives the checkbox list under the active tab.
  const filteredControlsByArea = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();
    if (!normalizedSearch) return allControlsByArea;

    const matching = controls.filter((control) =>
      [control.name, control.description, control.code]
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearch),
    );
    return groupByArea(matching);
  }, [allControlsByArea, controls, searchQuery]);

  const areasWithControls = ALL_AREAS.filter(
    (area) => allControlsByArea[area].length > 0,
  );

  // If the active tab has no search matches but another tab does, jump to
  // the first tab that does — avoids a search that looks like it found
  // nothing just because the wrong tab is open.
  useEffect(() => {
    if (!searchQuery.trim()) return;
    if (filteredControlsByArea[activeArea]?.length > 0) return;
    const firstMatch = areasWithControls.find(
      (area) => filteredControlsByArea[area].length > 0,
    );
    if (firstMatch) setActiveArea(firstMatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const activeControls = filteredControlsByArea[activeArea] ?? [];

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          size={16}
        />
        <Input
          value={searchQuery}
          onChange={(value) => setSearchQuery(String(value))}
          placeholder="Search controls by name, code, or description"
          className="pl-10"
        />
      </div>

      <div className="flex gap-5 overflow-x-auto border-b border-gray-100">
        {areasWithControls.map((area) => {
          const total = allControlsByArea[area].length;
          const selectedInArea = allControlsByArea[area].filter((c) =>
            selectedControlIds.includes(c.id),
          ).length;

          return (
            <TabButton
              key={area}
              label={`${AREA_LABELS[area]} (${selectedInArea}/${total})`}
              isActive={activeArea === area}
              onClick={() => setActiveArea(area)}
            />
          );
        })}
      </div>

      <div className="space-y-2">
        {activeControls.length === 0 ? (
          <p className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
            No matching controls in this category.
          </p>
        ) : (
          activeControls.map((control) => {
            const isSelected = selectedControlIds.includes(control.id);

            return (
              <div
                key={control.id}
                className={[
                  'flex items-center justify-between gap-3 rounded-xl border px-3 py-3 transition-colors',
                  isSelected
                    ? 'border-pos-primary bg-white'
                    : 'border-gray-200 bg-white hover:border-pos-primary/40',
                ].join(' ')}
              >
                <label
                  htmlFor={`access-control-${control.id}`}
                  className="min-w-0 cursor-pointer"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-black">
                      {control.name}
                    </span>
                    <span className="rounded-full bg-app-bg px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-gray-700">
                      {control.code}
                    </span>
                  </div>
                  <p className="mt-1 text-xs leading-5 text-gray-600">
                    {control.description}
                  </p>
                </label>
                <Toggle
                  id={`access-control-${control.id}`}
                  checked={isSelected}
                  onChange={() => onToggleControl(control.id)}
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
